/**
 * Ledger Family Page (DRR-109)
 *
 * Read-only view of a resident's ledger for family members / responsible parties.
 * Scoped by RequireScope authorization — family can only see their linked resident.
 *
 * Displays: balance, deposit history, spending summary.
 * Does NOT expose: social tokens (private), settlement details, chain hashes.
 *
 * Mantine v8.x only. No MUI.
 */

import { useTenantId } from '@/auth';
import { useMyActor } from '@/hooks/useMyActor';
// eslint-disable-next-line no-restricted-imports -- DRR-109 ledger queries not yet in codegen

import {
  useGetFamilyLedgerAccountQuery,
  useGetFamilyLedgerTransfersQuery,
} from '@/generated/graphql';
import {
  IconWallet,
  IconCoin,
  IconReceipt,
  IconAlertTriangle,
} from '@tabler/icons-react';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  Table,
  Paper,
  ThemeIcon,
  Alert,
  Loader,
  SimpleGrid,
} from '@mantine/core';
// =========================================================================
// Local types (pending codegen — mirrors backend LedgerTransfer shape)
// =========================================================================

interface LedgerTransferRow {
  transferId: string;
  amountCents: number;
  tokenType: string;
  serviceCategory?: string;
  status: string;
  createdAt: string;
}

// =========================================================================
// GraphQL Operations (pending codegen — matches backend LedgerQuery)
// =========================================================================

// =========================================================================
// Component
// =========================================================================

export function LedgerFamilyPage() {
  const tenantId = useTenantId() ?? '';
  const tenantNum = tenantId ? parseInt(tenantId, 10) : 0;

  // Step 1: Get current actor (family member linked to resident) — canonical hook
  const { actorId: resolvedActorId, loading: actorLoading } = useMyActor();
  const actorId = resolvedActorId ?? '';

  // Step 2: Get ledger account (scoped by RequireScope on backend)
  const {
    data: accountData,
    loading: accountLoading,
    error: accountError,
  } = useGetFamilyLedgerAccountQuery({
    variables: { tenantNum, actorId },
    skip: !tenantId || !actorId,
  });

  // Step 3: Get payment transfers only (family doesn't see social tokens)
  const {
    data: transfersData,
    loading: transfersLoading,
  } = useGetFamilyLedgerTransfersQuery({
    variables: { tenantNum },
    skip: !tenantId,
  });

  if (!tenantId) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow" title="No Tenant Selected">
          Please select a tenant to view resident ledger.
        </Alert>
      </Container>
    );
  }

  const loading = actorLoading || accountLoading;

  if (accountError) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Ledger">
          {accountError.message}
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container size="lg" py="xl">
        <Group justify="center" py="xl">
          <Loader size="lg" />
        </Group>
      </Container>
    );
  }

  const account = (accountData as { ledgerAccount?: { accountId: string; tenantNum: number; actorId: string; balanceCents: number; pointsBalance: number; status: string; createdAt: string } } | undefined)?.ledgerAccount;
  const allTransfers: LedgerTransferRow[] = (transfersData as { ledgerTransfers?: LedgerTransferRow[] } | undefined)?.ledgerTransfers ?? [];

  // Family view: only payment transfers (social tokens are private)
  const paymentTransfers = allTransfers.filter(
    (t) => t.tokenType === 'PAYMENT'
  );

  const formatCurrency = (cents: number) => `$${(cents / 100).toFixed(2)}`;
  const balanceCents = account?.balanceCents ?? 0;

  // Compute spending summary by category
  const spendingMap = new Map<string, { totalCents: number; count: number }>();
  for (const t of paymentTransfers) {
    const cat = t.serviceCategory ?? 'Other';
    const prev = spendingMap.get(cat) ?? { totalCents: 0, count: 0 };
    spendingMap.set(cat, {
      totalCents: prev.totalCents + Math.abs(t.amountCents),
      count: prev.count + 1,
    });
  }
  const spending = Array.from(spendingMap.entries()).map(([category, data]) => ({
    category,
    totalCents: data.totalCents,
    transactionCount: data.count,
  }));

  // Compute deposit total (positive amounts = deposits)
  const totalDeposited = paymentTransfers
    .filter((t) => t.amountCents > 0)
    .reduce((sum: number, t) => sum + t.amountCents, 0);
  const totalSpent = paymentTransfers
    .filter((t) => t.amountCents < 0)
    .reduce((sum: number, t) => sum + Math.abs(t.amountCents), 0);

  // Deposits only (positive amountCents)
  const deposits = paymentTransfers.filter(
    (t) => t.amountCents > 0
  );

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Resident Account</Title>
            <Text c="dimmed" size="sm">
              Read-only view of your resident&apos;s account
            </Text>
          </div>
          <Badge color="blue" variant="light" size="lg">
            Family View
          </Badge>
        </Group>

        {/* Balance Cards */}
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
          <Card withBorder p="lg" radius="md">
            <Group gap="xs">
              <ThemeIcon variant="light" color="blue" size="xl" radius="md">
                <IconWallet size={24} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Current Balance
                </Text>
                <Text size="xl" fw={700}>
                  {formatCurrency(balanceCents)}
                </Text>
              </div>
            </Group>
          </Card>

          <Card withBorder p="lg" radius="md">
            <Group gap="xs">
              <ThemeIcon variant="light" color="green" size="xl" radius="md">
                <IconCoin size={24} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Total Deposited
                </Text>
                <Text size="xl" fw={700}>
                  {formatCurrency(totalDeposited)}
                </Text>
              </div>
            </Group>
          </Card>

          <Card withBorder p="lg" radius="md">
            <Group gap="xs">
              <ThemeIcon variant="light" color="orange" size="xl" radius="md">
                <IconReceipt size={24} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Total Spent
                </Text>
                <Text size="xl" fw={700}>
                  {formatCurrency(totalSpent)}
                </Text>
              </div>
            </Group>
          </Card>
        </SimpleGrid>

        {/* Spending Summary by Category */}
        <Card withBorder p="md">
          <Text fw={600} mb="md">
            Spending by Service
          </Text>
          <Paper withBorder>
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Service</Table.Th>
                  <Table.Th>Total Spent</Table.Th>
                  <Table.Th>Transactions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {spending.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={3}>
                      <Text ta="center" c="dimmed" py="md">
                        No spending activity yet
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  spending.map((s) => (
                    <Table.Tr key={s.category}>
                      <Table.Td>{s.category}</Table.Td>
                      <Table.Td fw={500}>{formatCurrency(s.totalCents)}</Table.Td>
                      <Table.Td>{s.transactionCount}</Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </Paper>
        </Card>

        {/* Deposit History */}
        <Card withBorder p="md">
          <Text fw={600} mb="md">
            Deposit History
          </Text>
          <Paper withBorder>
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Amount</Table.Th>
                  <Table.Th>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {transfersLoading ? (
                  <Table.Tr>
                    <Table.Td colSpan={3}>
                      <Group justify="center" py="md">
                        <Loader size="sm" />
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ) : deposits.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={3}>
                      <Text ta="center" c="dimmed" py="md">
                        No deposits yet
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  deposits.map((d) => (
                    <Table.Tr key={d.transferId}>
                      <Table.Td>
                        {new Date(d.createdAt as string).toLocaleDateString()}
                      </Table.Td>
                      <Table.Td fw={500}>
                        {formatCurrency(d.amountCents as number)}
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          color={d.status === 'CONFIRMED' ? 'green' : 'yellow'}
                          size="sm"
                          variant="light"
                        >
                          {d.status as string}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </Paper>
        </Card>
      </Stack>
    </Container>
  );
}

export default LedgerFamilyPage;
